import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Box, Paper, Typography, Stack, Chip, Avatar, ButtonBase, Divider } from '@mui/material';
import {
  Today as TodayIcon,
  Warning as AlertIcon,
  History as RecentIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { format, startOfDay, endOfDay, isToday, isTomorrow } from 'date-fns';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import HorsePhotoAvatar from '../../components/HorsePhotoAvatar';
import { appointmentApi, AppointmentListItem, Horse, horseApi } from '../../utils/api';
import { withOfflineFallback, readAppointmentsInRange, readFlaggedHorses } from '../../offline';
import { getRecentlyViewed, subscribeRecentlyViewed, RecentPatient } from './recentlyViewed';
import { AuthContext } from '../../auth/AuthContext';
import { isCancelled } from '../Appointments/appointmentStatus';

export interface InsightsPanelProps {
  horses: Horse[];
}

const SectionCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  count?: number;
  accent?: string;
  children: React.ReactNode;
}> = ({ icon, label, count, accent, children }) => (
  <Paper sx={{ p: 1.5, borderRadius: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, color: accent || 'text.primary' }}>
      {icon}
      <Typography
        variant="subtitle2"
        sx={{
          fontSize: '0.72rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          flexGrow: 1,
          color: 'inherit',
        }}
      >
        {label}
      </Typography>
      {count !== undefined && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
          {count}
        </Typography>
      )}
    </Box>
    {children}
  </Paper>
);

const PatientRow: React.FC<{
  horseId?: string;
  name: string;
  patientId?: string;
  meta?: string;
  rightLabel?: string;
  rightColor?: string;
  onClick?: () => void;
  highlight?: boolean;
}> = ({ horseId, name, patientId, meta, rightLabel, rightColor, onClick, highlight }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      width: '100%',
      py: 0.6,
      px: 0.5,
      borderRadius: 1,
      textAlign: 'left',
      transition: 'background 120ms',
      '&:hover': { bgcolor: 'rgba(139,94,52,0.06)' },
    }}
  >
    <HorsePhotoAvatar horseId={horseId} size={26} sx={{ flexShrink: 0 }} />
    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.78rem',
          fontWeight: highlight ? 600 : 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 1.2,
        }}
      >
        {name}
        {patientId && (
          <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'text.secondary', ml: 0.5 }}>
            {patientId}
          </Box>
        )}
      </Typography>
      {meta && (
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.68rem',
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            lineHeight: 1.2,
          }}
        >
          {meta}
        </Typography>
      )}
    </Box>
    {rightLabel && (
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.68rem',
          fontWeight: 600,
          color: rightColor || 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}
      >
        {rightLabel}
      </Typography>
    )}
  </ButtonBase>
);

/**
 * Right-hand insights rail for the Patients page.
 *
 *  • Today — appointments scheduled for today (server-fetched)
 *  • Need attention — patients in the visible list with critical alerts
 *  • Recently viewed — local cache of charts the user opened (this device)
 *
 * The panel is intentionally lightweight: it doesn't fire heavy queries on
 * mount (today's appointments only). "Need attention" derives from the
 * already-loaded patient list so opening this page costs at most one extra
 * round trip.
 */
const InsightsPanel: React.FC<InsightsPanelProps> = ({ horses }) => {
  const navigate = useNavigate();
  const formatInstant = useFormatInstant();
  const uid = useContext(AuthContext)?.user?.uid;
  const [recent, setRecent] = useState<RecentPatient[]>(() => getRecentlyViewed(uid));

  // Re-render when the recently-viewed list changes (e.g. user opens a chart
  // in another tab or this tab navigates back here), or the signed-in user does.
  useEffect(() => {
    setRecent(getRecentlyViewed(uid));
    return subscribeRecentlyViewed(() => setRecent(getRecentlyViewed(uid)));
  }, [uid]);

  const todayRange = useMemo(() => {
    const now = new Date();
    return { from: startOfDay(now).toISOString(), to: endOfDay(now).toISOString() };
  }, []);

  const { data: appointments = [], isLoading: aptLoading } = useQuery<AppointmentListItem[]>(
    ['insights-today', todayRange.from],
    withOfflineFallback(
      () => appointmentApi.list({ dateFrom: todayRange.from, dateTo: todayRange.to }),
      () => readAppointmentsInRange({ dateFrom: todayRange.from, dateTo: todayRange.to }).then((p) => p.data as AppointmentListItem[]),
    ),
    { staleTime: 60_000 },
  );

  // Sort today's appointments by time, soonest first
  const sortedAppts = useMemo(
    () => appointments
      .filter((a) => !isCancelled(a.status))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [appointments],
  );

  // Clinic-wide flagged patients (server-filtered). Previously this was
  // derived from `horses` (the in-memory current page), which silently
  // missed flagged patients on page 2+. The dedicated endpoint returns
  // ALL critical-alert horses across the clinic regardless of pagination.
  // Suppress the type prop since `horses` is still passed for API stability
  // but is no longer the source of truth for this section.
  void horses;
  // Offline: derive the same list from the replica (readFlaggedHorses mirrors
  // GET /horses/flagged incl. criticalAlerts) so the rail never shows "No
  // patients with critical alerts" just because the request failed.
  const { data: flaggedAll = [] } = useQuery(
    ['insights-flagged'],
    withOfflineFallback(
      () => horseApi.listFlagged(),
      () => readFlaggedHorses() as any,
    ),
    { staleTime: 5 * 60_000 },
  );
  // Display cap stays at 6 so the rail stays scannable; the section count
  // shows the full total so vets know there are more behind the link.
  const flagged = flaggedAll.slice(0, 6);
  const flaggedTotal = flaggedAll.length;

  return (
    <Stack spacing={1.25} sx={{ width: '100%' }}>
      <SectionCard
        icon={<TodayIcon sx={{ fontSize: 16 }} />}
        label="Today"
        count={sortedAppts.length}
        accent="#3B2E1A"
      >
        {aptLoading ? (
          <Typography variant="caption" color="text.secondary">Loading…</Typography>
        ) : sortedAppts.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No appointments today</Typography>
        ) : (
          <Stack spacing={0.25}>
            {sortedAppts.slice(0, 6).map((apt) => (
              <PatientRow
                key={apt.id}
                horseId={apt.horse?.id}
                name={apt.horse?.name || 'Unknown patient'}
                meta={apt.type}
                // INSTANT (Appointment.date) — the clinic's wall clock, not the browser's.
                rightLabel={formatInstant(apt.date, 'h:mma').toLowerCase()}
                onClick={() => apt.horse?.id && navigate(`/patients/${apt.horse.id}`)}
              />
            ))}
            {sortedAppts.length > 6 && (
              <Box sx={{ pt: 0.5, textAlign: 'right' }}>
                <ButtonBase
                  onClick={() => navigate('/appointments')}
                  sx={{ fontSize: '0.7rem', color: 'primary.main', fontWeight: 600, textTransform: 'none' }}
                >
                  View schedule →
                </ButtonBase>
              </Box>
            )}
          </Stack>
        )}
      </SectionCard>

      <SectionCard
        icon={<AlertIcon sx={{ fontSize: 16 }} />}
        label="Need attention"
        count={flaggedTotal}
        accent={flaggedTotal > 0 ? '#C62828' : 'text.primary'}
      >
        {flaggedTotal === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No patients with critical alerts
          </Typography>
        ) : (
          <Stack spacing={0.25}>
            {flagged.map((h) => {
              const critical = h.criticalAlerts ?? [];
              const firstAlert = critical[0]?.text || 'Critical alert';
              return (
                <PatientRow
                  key={h.id}
                  horseId={h.id}
                  name={h.name}
                  patientId={h.patientId}
                  meta={firstAlert}
                  rightLabel={critical.length > 1 ? `+${critical.length - 1}` : undefined}
                  rightColor="#C62828"
                  onClick={() => navigate(`/patients/${h.id}`)}
                  highlight
                />
              );
            })}
            {flaggedTotal > flagged.length && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.68rem', pl: 0.5, pt: 0.25 }}
              >
                +{flaggedTotal - flagged.length} more flagged across the clinic
              </Typography>
            )}
          </Stack>
        )}
      </SectionCard>

      <SectionCard
        icon={<RecentIcon sx={{ fontSize: 16 }} />}
        label="Recently viewed"
        count={recent.length}
      >
        {recent.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            Open a patient chart to start building your list
          </Typography>
        ) : (
          <Stack spacing={0.25}>
            {recent.slice(0, 6).map((p) => (
              <PatientRow
                key={p.id}
                horseId={p.id}
                name={p.name}
                patientId={p.patientId}
                meta={[p.breed, p.ownerName].filter(Boolean).join(' · ') || undefined}
                rightLabel={describeRecent(p.viewedAt)}
                rightColor="text.secondary"
                onClick={() => navigate(`/patients/${p.id}`)}
              />
            ))}
          </Stack>
        )}
      </SectionCard>

      {flagged.length === 0 && sortedAppts.length === 0 && recent.length === 0 && (
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            boxShadow: 'none',
            border: '1px dashed',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <TimeIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
            Insights light up as your day fills in
          </Typography>
        </Paper>
      )}
    </Stack>
  );
};

function describeRecent(ts: number): string {
  const d = new Date(ts);
  if (isToday(d)) return format(d, 'h:mma').toLowerCase();
  if (isTomorrow(d)) return 'tomorrow';
  const days = Math.floor((Date.now() - ts) / (1000 * 60 * 60 * 24));
  if (days < 7) return `${days}d ago`;
  return format(d, 'MMM d');
}

// Unused import kept for future "load more" chip variants
void Chip;
void Divider;

export default InsightsPanel;
